import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Check, X, Loader2, ScrollText, Eye } from 'lucide-react';

export const PendingModelLettersCard = () => {
  const [letters, setLetters] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/admin/model-letters/pending');
      setLetters(res.data.letters || []);
    } catch (e) {
      console.error('Failed to load model letters', e);
    }
  };

  useEffect(() => { load(); }, []);

  const preview = async (l) => {
    setBusyId(`preview-${l.id}`);
    try {
      const res = await api.get(`/courses/${l.course_id}/model-letter/preview`, { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
    } catch {
      toast.error('Could not open preview');
    } finally {
      setBusyId(null);
    }
  };

  const review = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/admin/model-letters/${id}/${action}`);
      toast.success(`Model letter ${action === 'approve' ? 'approved' : 'rejected'}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="mt-6" style={{ borderColor: '#E3E1DB' }} data-testid="pending-model-letters-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
          <ScrollText className="h-5 w-5" /> Pending Letters of Model Acquisition
        </CardTitle>
      </CardHeader>
      <CardContent>
        {letters.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: '#5C635A' }} data-testid="no-pending-model-letters">
            No pending model letters.
          </p>
        ) : (
          <div className="space-y-4">
            {letters.map((l) => (
              <div key={l.id} className="p-4 rounded-lg border" style={{ backgroundColor: '#FFF8E7', borderColor: '#F59E0B' }} data-testid={`pending-model-letter-${l.id}`}>
                <div className="flex flex-col md:flex-row md:justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-semibold" style={{ color: '#163329' }}>{l.course_title}</h4>
                    <p className="text-sm mb-2" style={{ color: '#5C635A' }}>Teacher: {l.teacher_name} ({l.teacher_email})</p>
                    {l.mode === 'pdf' ? (
                      <p className="text-sm p-3 rounded-lg bg-white border" style={{ borderColor: '#E3E1DB', color: '#163329' }}>
                        Uploaded PDF letter: <strong>{l.template_filename}</strong> ({Object.keys(l.positions || {}).length} field positions marked). Use Preview to check it.
                      </p>
                    ) : (
                      <div className="p-3 rounded-lg text-sm max-h-40 overflow-y-auto whitespace-pre-wrap bg-white border" style={{ borderColor: '#E3E1DB', color: '#163329' }}>
                        {l.text}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-row md:flex-col gap-2">
                    <Button variant="outline" onClick={() => preview(l)} disabled={busyId === `preview-${l.id}`} data-testid={`preview-model-letter-${l.id}`}>
                      {busyId === `preview-${l.id}` ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />} Preview
                    </Button>
                    <Button onClick={() => review(l.id, 'approve')} disabled={busyId === l.id} style={{ backgroundColor: '#2E7D32' }} data-testid={`approve-model-letter-${l.id}`}>
                      {busyId === l.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} Approve
                    </Button>
                    <Button variant="destructive" onClick={() => review(l.id, 'reject')} disabled={busyId === l.id} data-testid={`reject-model-letter-${l.id}`}>
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
